/**
 * EC Insights Card — surfaces actionable findings from soil EC time-series.
 *
 * Renders ONLY when the user has WH52 probes with history. Three states:
 *   - Loading → silent (no skeleton; this is below-the-fold supplemental)
 *   - coverage_status='no_history' or 'no_ec_data' → renders null
 *   - coverage_status='ok' with insights → renders one card per finding
 *   - coverage_status='ok' with empty insights → renders "all clear" tile
 *
 * Each insight card has:
 *   - Severity-tinted left border (high=rose, medium=amber, low=zinc)
 *   - Title + plain-english detail
 *   - Recommendation in a quote-style block
 *   - Channel pill + EC reading + band label
 *
 * Polls every 5 minutes (server-side data only refreshes every 15 min anyway).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const REFRESH_MS = 5 * 60 * 1000;

const SEVERITY_STYLE = {
  high: {
    border: 'border-l-rose-500',
    badgeBg: 'bg-rose-100 dark:bg-rose-950/40',
    badgeText: 'text-rose-900 dark:text-rose-200',
    label: 'High',
  },
  medium: {
    border: 'border-l-amber-500',
    badgeBg: 'bg-amber-100 dark:bg-amber-950/40',
    badgeText: 'text-amber-900 dark:text-amber-200',
    label: 'Medium',
  },
  low: {
    border: 'border-l-zinc-400',
    badgeBg: 'bg-zinc-100 dark:bg-zinc-800',
    badgeText: 'text-zinc-700 dark:text-zinc-300',
    label: 'Low',
  },
};

const TYPE_ICON = {
  fertilizer_burn: '🔥',
  leaching: '💧',
  salt_buildup: '🧂',
};

export default function ECInsightsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInsights = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ecowitt/ec-insights`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      // Silently no-op — this is supplementary content, errors shouldn't
      // disrupt the dashboard. The auth wrapper + history TTL guarantee
      // we'll get a clean response next refresh.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInsights();
    const id = setInterval(fetchInsights, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchInsights]);

  // Hide entirely when user has no relevant data — never show empty/loading noise
  if (loading) return null;
  if (!data) return null;
  if (data.coverage_status === 'no_history' || data.coverage_status === 'no_ec_data') return null;

  const insights = data.insights || [];

  return (
    <section className="space-y-3" data-testid="ec-insights-card">
      <div className="flex items-baseline justify-between">
        <h3 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
          Soil EC Insights
        </h3>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {data.channels_evaluated} WH52 {data.channels_evaluated === 1 ? 'channel' : 'channels'} · last {data.data_window_days}d
        </span>
      </div>

      {insights.length === 0 ? (
        // All-clear state — gentle confirmation, not a celebration
        <div
          className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/30 px-4 py-3"
          data-testid="ec-insights-all-clear"
        >
          <p className="text-sm text-emerald-900 dark:text-emerald-200">
            <span className="font-medium">All clear.</span> No fertilizer-burn, leaching, or salt-buildup signals detected in the last {data.data_window_days} days.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {insights.map((ins, idx) => {
            const sev = SEVERITY_STYLE[ins.severity] || SEVERITY_STYLE.low;
            return (
              <div
                key={`${ins.channel}-${ins.type}-${idx}`}
                className={`rounded-lg border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 border-l-4 ${sev.border} p-4`}
                data-testid={`ec-insight-${ins.type}-${ins.channel}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg" aria-hidden>{TYPE_ICON[ins.type] || '•'}</span>
                    <h4 className="font-semibold text-stone-900 dark:text-stone-100 text-sm">
                      {ins.title}
                    </h4>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${sev.badgeBg} ${sev.badgeText} flex-shrink-0`}>
                    {sev.label}
                  </span>
                </div>

                <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed mb-3">
                  {ins.detail}
                </p>

                <div className="rounded-md bg-stone-50 dark:bg-zinc-950/50 border-l-2 border-stone-300 dark:border-zinc-700 px-3 py-2 mb-3">
                  <p className="text-xs uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-1">
                    Recommendation
                  </p>
                  <p className="text-sm text-stone-800 dark:text-stone-200 leading-relaxed">
                    {ins.recommendation}
                  </p>
                </div>

                <div className="flex items-center justify-between text-xs text-stone-500 dark:text-stone-400">
                  <span>Channel {ins.channel} · {Math.round(ins.ec_us_cm)} µS/cm · {ins.ec_band}</span>
                  {ins.detected_at && (
                    <span title={ins.detected_at}>
                      detected {new Date(ins.detected_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
